import { FC, memo, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Modal,
  Skeleton,
  Table,
  TimePicker,
  Tooltip,
} from 'antd';
import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/lib/table';
import { ScheduleDay } from '../../../../hooks/components/useClinicSettingsDetail';
import { getUtcLabel } from '../../../../hooks/shared/useUtcLabel';
import {
  compactSpecialDates,
  expandSpecialDateRanges,
  formatSpecialDateDisplayValues,
} from '../../../../hooks/components/useClinicSettingsDetail/schedule/specialDates';
import styles from './styles.module.css';

const { RangePicker } = DatePicker;

interface HolidayDisplayGroup {
  start: string;
  end: string;
  label: string;
  key: string;
}

interface Props {
  schedule: ScheduleDay[];
  holidays: string[];
  onScheduleChange: (
    day: string,
    field: 'start' | 'end',
    value: string | null,
  ) => void;
  onToggleDay: (day: string) => void;
  onAddHoliday: (date: string) => void;
  onRemoveHoliday: (date: string) => void;
  timezone?: string;
  /** Состояние загрузки данных расписания */
  loading?: boolean;
  disabled?: boolean;
  /** Заголовок столбца дня. По умолчанию «День недели» */
  dayColumnTitle?: string;
  /** Заголовок столбца начала. По умолчанию «Начало смены» */
  startColumnTitle?: string;
  /** Заголовок столбца окончания. По умолчанию «Окончание смены» */
  endColumnTitle?: string;
  /** Подсказка модального окна. По умолчанию — текст для графика работы операторов */
  modalHint?: string;
  /** Дополнительный tooltip «?» рядом с заголовком окончания */
  endColumnHelpTooltip?: string;
}

export const ScheduleTable: FC<Props> = memo(
  ({
    schedule,
    holidays,
    onScheduleChange,
    onToggleDay,
    onAddHoliday,
    onRemoveHoliday,
    timezone,
    loading = false,
    disabled = false,
    dayColumnTitle = 'День недели',
    startColumnTitle = 'Начало смены',
    endColumnTitle = 'Окончание смены',
    modalHint = 'Выбранные даты будут исключены из графика работы операторов',
    endColumnHelpTooltip,
  }) => {
    const tzHint = useMemo(
      () => `Часовой пояс ${timezone ?? getUtcLabel()}`,
      [timezone],
    );
    const [holidayModalOpen, setHolidayModalOpen] = useState(false);
    // Локальное состояние диалога — чтобы кнопка "Сохранить" активировалась только при изменениях
    const [localHolidays, setLocalHolidays] = useState<string[]>([]);
    const [isDirty, setIsDirty] = useState(false);

    const openModal = () => {
      if (disabled) return;
      setLocalHolidays(expandSpecialDateRanges(holidays, 'local'));
      setIsDirty(false);
      setHolidayModalOpen(true);
    };

    const handleModalSave = () => {
      if (disabled) return;
      // синхронизируем: удалённые — удалить, добавленные — добавить
      holidays.forEach((d) => {
        if (!localHolidays.includes(d)) onRemoveHoliday(d);
      });
      localHolidays.forEach((d) => {
        if (!holidays.includes(d)) onAddHoliday(d);
      });
      setHolidayModalOpen(false);
    };

    const handleLocalAddRange = (dateStrings: [string, string]) => {
      if (disabled) return;
      const [start, end] = dateStrings;

      if (!start || !end) {
        return;
      }

      const nextHolidays = expandSpecialDateRanges(
        [...localHolidays, { start, end }],
        'local',
      );
      const unchanged =
        nextHolidays.length === localHolidays.length &&
        nextHolidays.every((date, index) => date === localHolidays[index]);

      if (!unchanged) {
        setLocalHolidays(nextHolidays);
        setIsDirty(true);
      }
    };

    const handleLocalRemove = ({ start, end }: HolidayDisplayGroup) => {
      if (disabled) return;
      const datesToRemove = new Set(
        expandSpecialDateRanges([{ start, end }], 'local'),
      );

      setLocalHolidays((prev) =>
        prev.filter((date) => !datesToRemove.has(date)),
      );
      setIsDirty(true);
    };

    const holidaysDisplayValues = useMemo(
      () => formatSpecialDateDisplayValues(holidays),
      [holidays],
    );
    const holidaysDisplay = holidaysDisplayValues.join(', ');

    const localHolidayGroups: HolidayDisplayGroup[] = useMemo(
      () =>
        compactSpecialDates(localHolidays, 'local').map(({ start, end }) => ({
          start,
          end,
          label: start === end ? start : `${start}–${end}`,
          key: `${start}-${end}`,
        })),
      [localHolidays],
    );

    const columns: ColumnsType<ScheduleDay> = useMemo(
      () => [
        {
          title: dayColumnTitle,
          key: 'day',
          width: '33.33%',
          render: (_, record) => {
            const active = record.start !== null || record.end !== null;
            return (
              <span
                className={
                  active ? styles.dayBadgeActive : styles.dayBadgeInactive
                }
                aria-disabled={disabled}
                onClick={() => {
                  if (!disabled) onToggleDay(record.day);
                }}
              >
                {record.label}
              </span>
            );
          },
        },
        {
          title: (
            <Tooltip title={tzHint}>
              <span className={styles.colTitle}>{startColumnTitle}</span>
            </Tooltip>
          ),
          key: 'start',
          width: '33.33%',
          render: (_, record) => {
            const active = record.start !== null || record.end !== null;
            const endDayjs = record.end ? dayjs(record.end, 'HH:mm') : null;
            return (
              <Tooltip title={tzHint}>
                <TimePicker
                  className={styles.timePicker}
                  format="HH:mm"
                  value={record.start ? dayjs(record.start, 'HH:mm') : null}
                  onChange={(_, str) => {
                    const newStart = (str as string) || null;
                    onScheduleChange(record.day, 'start', newStart);
                    // Сбрасываем окончание, если оно стало <= нового начала
                    if (newStart && record.end) {
                      const s = dayjs(newStart, 'HH:mm');
                      const e = dayjs(record.end, 'HH:mm');
                      if (e.isBefore(s) || e.isSame(s)) {
                        onScheduleChange(record.day, 'end', null);
                      }
                    }
                  }}
                  placeholder="--:--"
                  allowClear={false}
                  disabled={disabled || !active}
                  disabledTime={() => {
                    if (!endDayjs) return {};
                    const endH = endDayjs.hour();
                    const endM = endDayjs.minute();
                    return {
                      disabledHours: () =>
                        Array.from({ length: 24 - endH }, (_, i) => endH + i),
                      disabledMinutes: (selectedHour: number) =>
                        selectedHour === endH - 1
                          ? Array.from(
                              { length: 60 - endM },
                              (_, i) => endM + i,
                            )
                          : [],
                    };
                  }}
                />
              </Tooltip>
            );
          },
        },
        {
          title: (
            <span className={styles.colTitleWrap}>
              <Tooltip title={tzHint}>
                <span className={styles.colTitle}>{endColumnTitle}</span>
              </Tooltip>
              {endColumnHelpTooltip && (
                <Tooltip title={endColumnHelpTooltip}>
                  <QuestionCircleOutlined className={styles.colHelpIcon} />
                </Tooltip>
              )}
            </span>
          ),
          key: 'end',
          width: '33.33%',
          render: (_, record) => {
            const active = record.start !== null || record.end !== null;
            const startDayjs = record.start
              ? dayjs(record.start, 'HH:mm')
              : null;
            return (
              <Tooltip title={tzHint}>
                <TimePicker
                  className={styles.timePicker}
                  format="HH:mm"
                  value={record.end ? dayjs(record.end, 'HH:mm') : null}
                  onChange={(_, str) => {
                    const newEnd = (str as string) || null;
                    onScheduleChange(record.day, 'end', newEnd);
                    // Сбрасываем начало, если оно стало >= нового окончания
                    if (newEnd && record.start) {
                      const s = dayjs(record.start, 'HH:mm');
                      const e = dayjs(newEnd, 'HH:mm');
                      if (s.isAfter(e) || s.isSame(e)) {
                        onScheduleChange(record.day, 'start', null);
                      }
                    }
                  }}
                  placeholder="--:--"
                  allowClear={false}
                  disabled={disabled || !active}
                  disabledTime={() => {
                    if (!startDayjs) return {};
                    const startH = startDayjs.hour();
                    const startM = startDayjs.minute();
                    return {
                      disabledHours: () =>
                        Array.from({ length: startH }, (_, i) => i),
                      disabledMinutes: (selectedHour: number) =>
                        selectedHour === startH
                          ? Array.from({ length: startM + 1 }, (_, i) => i)
                          : [],
                    };
                  }}
                />
              </Tooltip>
            );
          },
        },
      ],
      [
        tzHint,
        dayColumnTitle,
        startColumnTitle,
        endColumnTitle,
        endColumnHelpTooltip,
        onScheduleChange,
        onToggleDay,
        disabled,
      ],
    );

    const localHolidayColumns: ColumnsType<HolidayDisplayGroup> = [
      {
        title: 'Даты',
        key: 'date',
        render: (_, record) => (
          <span className={styles.holidayDate}>{record.label}</span>
        ),
      },
      {
        key: 'action',
        width: 40,
        render: (_, record) => (
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined className={styles.deleteIcon} />}
            aria-label={`Удалить ${record.label}`}
            disabled={disabled}
            onClick={() => handleLocalRemove(record)}
          />
        ),
      },
    ];

    if (loading) {
      return (
        <div className={styles.scheduleWrap}>
          <Skeleton active paragraph={{ rows: 7 }} />
        </div>
      );
    }

    return (
      <div className={styles.scheduleWrap}>
        {/* Таблица расписания */}
        <Table
          className={styles.scheduleTable}
          dataSource={schedule.map((s) => ({ ...s, key: s.day }))}
          columns={columns}
          pagination={false}
          size="small"
          tableLayout="fixed"
        />

        {/* Особые даты — строка: заголовок | инпут с датами | кнопка */}
        <div className={styles.holidaysRow}>
          <span className={styles.holidaysTitle}>Особые даты (праздники)</span>
          <div className={styles.holidaysPill} title={holidaysDisplay}>
            <span className={styles.holidaysPillText}>
              {holidaysDisplay || (
                <span className={styles.holidaysPillEmpty}>—</span>
              )}
            </span>
            <Button
              type="link"
              size="small"
              onClick={openModal}
              className={styles.editHolidayBtn}
              disabled={disabled}
            >
              Изменить
            </Button>
          </div>
        </div>

        {/* Диалог Особые даты */}
        <Modal
          title="Особые даты"
          open={holidayModalOpen}
          onCancel={() => setHolidayModalOpen(false)}
          footer={
            <Button
              type="primary"
              disabled={!isDirty || disabled}
              onClick={handleModalSave}
            >
              Сохранить
            </Button>
          }
          width={400}
        >
          <p className={styles.modalHint}>{modalHint}</p>
          <RangePicker
            className={styles.datePicker}
            format="DD.MM.YYYY"
            placeholder={['Дата с', 'Дата по']}
            onChange={(_, dateStrings) => handleLocalAddRange(dateStrings)}
            value={null}
            disabled={disabled}
          />
          <div className={styles.modalTableWrap}>
            <Table
              className={styles.holidaysTable}
              dataSource={localHolidayGroups}
              columns={localHolidayColumns}
              pagination={false}
              size="small"
              scroll={{ y: 200 }}
            />
          </div>
        </Modal>
      </div>
    );
  },
);

ScheduleTable.displayName = 'ScheduleTable';
